// Vidyapeeth360 Aira Assistant — the PUBLIC marketing sites' single chat widget (bottom-right,
// brand-logo FAB, Aira blue-violet panel). Mounted on every public MARKETING page (Landing + the global
// MarketingAira gate in App.js) — never on tenant sites (they have the tenant AiraWidget) or inside
// the authenticated app (signed-in users get the in-app Aira with live data access).
//
// Behavior: a sales & support assistant for guests — LLM answers grounded server-side on the PUBLIC
// Help/KB corpus + marketing CMS (POST /public/aira/platform/chat), with Help Center deep links under
// each answer. A server-tracked session gives a visitor 50 free questions; past that the lead form
// gates the chat — submitting files a platform sales lead + a support ticket so the team can follow up.
import React, { useEffect, useRef, useState } from "react";
import { m, AnimatePresence, LazyMotion, domAnimation, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { track, utmLeadFields } from "../lib/analytics";
import { LOGO_ICON } from "./Logo";
import BrandWordmarkText, { BrandCopy } from "./BrandWordmarkText";
import { Markdown } from "./ui/Markdown";
import Captcha, { resetCaptcha } from "./Captcha";
import { Sparkles, Send, X, ArrowRight, ArrowUpRight, BookOpen, Phone, UserPlus } from "lucide-react";
import { requestBookDemo } from "./marketing/shared";

// ── Vidyapeeth360 Aira Assistant (bottom-right) ─────────────────────────────────────────────────
// The Landing page's ONLY chat widget: the real LLM bot (POST /public/aira/platform/chat), grounded
// on the FULL public Help/KB corpus server-side. Each answer ships the top-matched Help Center
// articles as deep links (/help?article=slug). A server-tracked session (localStorage id) gives a
// visitor 50 free questions; past that the lead form gates the chat — submitting files a platform
// sales lead + a support ticket (with transcript context) so the team can follow up. Aira uses a
// scoped cobalt-to-violet identity; the FAB keeps the brand mark.
const AIRA_SESSION_KEY = "vp360_aira_session";
const AIRA_STARTERS = [
  "What can Vidyapeeth360 do?",
  "How does pricing work?",
  "Does it support WhatsApp communication?",
  "Which privacy and DPDP-supporting controls does Vidyapeeth360 provide?",
  "Can you help with migration and setup?",
];

function getAiraSession() {
  try { return localStorage.getItem(AIRA_SESSION_KEY) || ""; } catch { return ""; }
}
function setAiraSession(id) {
  try { if (id) localStorage.setItem(AIRA_SESSION_KEY, id); } catch { /* storage unavailable */ }
}

export default function AiraAssistant({ onDemo }) {
  const [open, setOpen] = useState(false);
  const requestDemo = () => {
    if (onDemo) onDemo();
    else requestBookDemo({ placement: "aira_assistant", contextLabel: "the workflows discussed with Aira" });
  };
  // conversion signal: first open of the Aira widget this mount (props: surface only — no PII)
  const openTrackedRef = useRef(false);
  const assistantOpenerRef = useRef(null);
  const toggleOpen = () => {
    if (!open && !openTrackedRef.current) { openTrackedRef.current = true; track("aira_demo_interact", { surface: "assistant_widget" }); }
    if (!open) assistantOpenerRef.current = null;
    setOpen(!open);
  };
  // Any marketing surface can open the widget in place (e.g. the Contact page's "Chat with Aira"
  // card — sending a visitor away from the highest-intent page to find a chat bubble was a
  // per-page-review P1). Fires the same first-open conversion signal.
  useEffect(() => {
    const openInPlace = () => {
      const activeElement = document.activeElement;
      assistantOpenerRef.current = activeElement instanceof HTMLElement && activeElement !== document.body
        ? activeElement
        : null;
      if (!openTrackedRef.current) { openTrackedRef.current = true; track("aira_demo_interact", { surface: "assistant_widget_event" }); }
      setOpen(true);
    };
    window.addEventListener("vp-aira-open", openInPlace);
    return () => window.removeEventListener("vp-aira-open", openInPlace);
  }, []);
  const osReducedMotion = useReducedMotion();
  const [marketingPaused, setMarketingPaused] = useState(() => (
    typeof document !== "undefined" && document.documentElement.getAttribute("data-marketing-motion") === "paused"
  ));
  const motionOff = Boolean(osReducedMotion || marketingPaused);
  const [cookieClearance, setCookieClearance] = useState(0);
  const [marketingSurfaceObstructed, setMarketingSurfaceObstructed] = useState(false);
  const [mobileHeroZone, setMobileHeroZone] = useState(false);
  const [messages, setMessages] = useState([{ id: 0, role: "assistant", welcome: true }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);   // null until the first server response
  const [quotaGate, setQuotaGate] = useState(false);  // 50 free questions used → lead form required
  const [hardCap, setHardCap] = useState(false);      // 150-question post-lead cap → chat closed for good
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ school: "", name: "", phone: "", email: "" });
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadError, setLeadError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const scrollRef = useRef(null);
  const idRef = useRef(1);
  const fabRef = useRef(null);
  const inputRef = useRef(null);
  // WP-17: ask() clears the value programmatically (no onChange fires) — collapse the
  // auto-grown textarea back to one line whenever the input empties.
  useEffect(() => {
    if (!input && inputRef.current) inputRef.current.style.height = "";
  }, [input]);
  const leadSchoolRef = useRef(null);
  const leadOpenerTestIdRef = useRef("aira-lead-open");

  useEffect(() => {
    const root = document.documentElement;
    const syncPause = () => setMarketingPaused(root.getAttribute("data-marketing-motion") === "paused");
    syncPause();
    const observer = new MutationObserver(syncPause);
    observer.observe(root, { attributes: true, attributeFilter: ["data-marketing-motion"] });
    return () => observer.disconnect();
  }, []);

  // Dense mobile marketing diagrams deliberately own the full reading width. Suppress the floating
  // launcher only while one of those explicitly marked sections crosses the usable viewport; the
  // assistant panel still has its own close control if it was already open. This avoids covering
  // evidence or CTA copy without permanently moving the launcher on every marketing page.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function" || typeof IntersectionObserver === "undefined") return undefined;
    const mobile = window.matchMedia("(max-width: 639px)");
    const compact = window.matchMedia("(max-width: 899px)");
    let intersectionObserver = null;
    let observedTargets = [];
    const visibleTargets = new Set();

    const connect = () => {
      const nextTargets = [
        ...(mobile.matches ? document.querySelectorAll('[data-aira-fab-clearance="hide-mobile"]') : []),
        ...(compact.matches ? document.querySelectorAll('[data-aira-fab-clearance="hide-compact"]') : []),
      ];
      if (nextTargets.length === observedTargets.length && nextTargets.every((target, index) => target === observedTargets[index])) return;
      intersectionObserver?.disconnect();
      visibleTargets.clear();
      observedTargets = nextTargets;
      if (!observedTargets.length) {
        setMarketingSurfaceObstructed(false);
        return;
      }
      intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleTargets.add(entry.target);
          else visibleTargets.delete(entry.target);
        });
        setMarketingSurfaceObstructed(visibleTargets.size > 0);
      }, { rootMargin: "-76px 0px -24px 0px", threshold: 0 });
      observedTargets.forEach((target) => intersectionObserver.observe(target));
    };

    connect();
    const discoveryObserver = new MutationObserver(connect);
    discoveryObserver.observe(document.body, { childList: true, subtree: true });
    if (typeof mobile.addEventListener === "function") mobile.addEventListener("change", connect);
    else mobile.addListener?.(connect);
    if (typeof compact.addEventListener === "function") compact.addEventListener("change", connect);
    else compact.addListener?.(connect);
    return () => {
      intersectionObserver?.disconnect();
      discoveryObserver.disconnect();
      if (typeof mobile.removeEventListener === "function") mobile.removeEventListener("change", connect);
      else mobile.removeListener?.(connect);
      if (typeof compact.removeEventListener === "function") compact.removeEventListener("change", connect);
      else compact.removeListener?.(connect);
    };
  }, []);

  // VP-FAB (390px audit, 2026-08): on phones the launcher floated over the first viewport's
  // content — the landing hero's subcopy and, at 390×844, the /modules hero CTA column (mobile
  // marketing text/CTAs span nearly the full width, so bottom-right always collides above the
  // fold). Suppress the FAB on <640px viewports until the visitor scrolls past ~80% of one
  // viewport height — content owns the hero; the launcher appears once reading is underway.
  // Desktop is untouched, and the panel (opened via the vp-aira-open event by in-page CTAs)
  // never depends on FAB visibility.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mobile = window.matchMedia("(max-width: 639px)");
    const sync = () => setMobileHeroZone(mobile.matches && window.scrollY < window.innerHeight * 0.8);
    sync();
    window.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    if (typeof mobile.addEventListener === "function") mobile.addEventListener("change", sync);
    else mobile.addListener?.(sync);
    return () => {
      window.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
      if (typeof mobile.removeEventListener === "function") mobile.removeEventListener("change", sync);
      else mobile.removeListener?.(sync);
    };
  }, []);

  // Keep the assistant usable while the first-visit privacy banner is visible. The panel sits
  // immediately above the measured banner instead of competing with it for the same bottom edge.
  useEffect(() => {
    let observedBanner = null;
    let bannerResizeObserver = null;
    const syncCookieClearance = () => {
      const banner = document.querySelector('[data-testid="cookie-consent"]');
      if (banner !== observedBanner) {
        bannerResizeObserver?.disconnect();
        observedBanner = banner;
        if (banner && typeof ResizeObserver !== "undefined") {
          bannerResizeObserver = new ResizeObserver(syncCookieClearance);
          bannerResizeObserver.observe(banner);
        }
      }
      if (!banner) {
        setCookieClearance(0);
        return;
      }
      const rect = banner.getBoundingClientRect();
      setCookieClearance(Math.max(0, Math.ceil(window.innerHeight - rect.top + 12)));
    };
    syncCookieClearance();
    const bodyObserver = new MutationObserver(syncCookieClearance);
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", syncCookieClearance);
    return () => {
      bodyObserver.disconnect();
      bannerResizeObserver?.disconnect();
      window.removeEventListener("resize", syncCookieClearance);
    };
  }, []);

  const closeAssistant = () => {
    const returnTarget = assistantOpenerRef.current;
    assistantOpenerRef.current = null;
    setOpen(false);
    window.requestAnimationFrame(() => {
      if (returnTarget?.isConnected) returnTarget.focus();
      else fabRef.current?.focus();
    });
  };

  const closeLeadForm = () => {
    setLeadOpen(false);
    window.requestAnimationFrame(() => {
      document.querySelector(`[data-testid="${leadOpenerTestIdRef.current}"]`)?.focus();
    });
  };

  const openLeadForm = (event) => {
    leadOpenerTestIdRef.current = event.currentTarget.dataset.testid || "aira-lead-open";
    setLeadOpen(true);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const pristineWelcome = open && messages.length === 1 && messages[0]?.welcome && !busy && !leadOpen;
    el.scrollTo({
      top: pristineWelcome ? 0 : el.scrollHeight,
      behavior: pristineWelcome || motionOff ? "auto" : "smooth",
    });
  }, [messages, busy, leadOpen, motionOff, open]);
  // a11y: opening the panel moves focus into it; opening the lead form (incl. the quota gate
  // disabling the textarea mid-focus) moves focus to its first field — focus is never stranded
  // on a disabled control or lost to <body>.
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => { if (open && leadOpen && !leadDone) leadSchoolRef.current?.focus(); }, [open, leadOpen, leadDone]);
  useEffect(() => { if (open && leadDone) inputRef.current?.focus(); }, [leadDone, open]);

  const ask = async (preset) => {
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || busy || quotaGate) return;
    setInput("");
    setMessages((m) => [...m, { id: ++idRef.current, role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/public/aira/platform/chat", {
        message: text, session_id: getAiraSession() || undefined,
      });
      if (data?.session_id) setAiraSession(data.session_id);
      if (typeof data?.remaining === "number") setRemaining(data.remaining);
      // An EMPTY answer is the "no public grounding for this" branch — it does not open the lead
      // form, so the copy must point at a control the visitor can actually see. Flagging the
      // message as an error renders the CTA row (which carries the visible "Talk to sales").
      const answer = typeof data?.answer === "string" ? data.answer.trim() : "";
      setMessages((m) => [...m, {
        id: ++idRef.current, role: "assistant",
        text: answer || "Sorry, I don't have a public answer for that. Tap \"Talk to sales\" and our team will follow up.",
        ...(answer ? {} : { error: true }),
        articles: Array.isArray(data?.articles) ? data.articles.filter((a) => a && a.slug) : [],
        suggestions: Array.isArray(data?.suggestions) ? data.suggestions : [],
      }]);
      if (data?.quota_exhausted) {
        setQuotaGate(true);
        if (data.hard_cap) setHardCap(true);      // past the post-lead cap there is nothing to submit
        else setLeadOpen(true);
      }
    } catch (e) {
      const tooFast = e?.response?.status === 429;
      // A 429 is either the short in-memory burst limiter or the DURABLE hourly cap, and only the
      // server knows which. Surface its own message when it supplies one — telling a visitor who
      // hit the hourly cap to "wait a moment" left them failing for up to an hour.
      const serverDetail = typeof e?.response?.data?.detail === "string" ? e.response.data.detail.trim() : "";
      setMessages((m) => [...m, {
        id: ++idRef.current, role: "assistant",
        text: tooFast
          ? (serverDetail || "You've reached the chat limit for now. Wait a minute and try again — or leave your details and our team will get back to you.")
          : "Sorry, I'm unavailable right now. Leave your details below so our team can follow up.",
        error: true,
      }]);
      if (!tooFast) setLeadOpen(true);
    } finally { setBusy(false); }
  };

  const lastAssistantId = [...messages].reverse().find((m) => m.role === "assistant" && !m.welcome)?.id;
  const fabHidden = marketingSurfaceObstructed || (open && cookieClearance > 0) || (!open && mobileHeroZone);

  useEffect(() => {
    if (fabHidden && document.activeElement === fabRef.current) fabRef.current?.blur();
  }, [fabHidden]);
  // Captcha enforcement is server-side when configured; never hard-block the form on the widget.
  const leadValid = lead.school.trim() && (lead.phone.trim() || lead.email.trim());
  const submitLead = async () => {
    if (!leadValid || leadBusy) return;
    setLeadBusy(true); setLeadError("");
    try {
      const { data } = await api.post("/public/aira/platform/lead", {
        // CRO-6: UTM attribution rides the whitelisted `campaign` field, exactly like every other
        // lead surface — spread FIRST so it can never clobber the form keys.
        ...utmLeadFields(),
        session_id: getAiraSession() || undefined,
        school_name: lead.school.trim(), contact_name: lead.name.trim(),
        phone: lead.phone.trim(), email: lead.email.trim(),
        captcha_token: captchaToken || undefined,
      });
      // CRO-6: the Aira conversion signal — was completely untracked. `gated` = the quota gate
      // forced this form; the question count ships as a low-cardinality bucket, never a raw count.
      const asked = messages.filter((m) => m.role === "user").length;
      track("aira_lead_submitted", {
        gated: quotaGate,
        questions_bucket: asked === 0 ? "0" : asked <= 5 ? "1-5" : asked <= 20 ? "6-20" : asked <= 49 ? "21-49" : "50+",
      });
      setLeadDone(true); setLeadOpen(false); setQuotaGate(false);
      setMessages((m) => [...m, {
        id: ++idRef.current, role: "assistant",
        text: data?.message || "Thank you — your enquiry has been sent to our sales and support team.",
      }]);
    } catch (e) {
      // CRO-5: a broken lead form must be analytically visible, not silent.
      track("lead_submit_failed", { placement: "aira_assistant", http_status: e?.response?.status ?? 0 });
      setLeadError("Couldn't send your details. Please try again, or email sales@vidyapeeth360.com and we'll pick it up from there.");
    } finally { resetCaptcha(); setLeadBusy(false); }
  };

  const Chip = ({ q }) => (
    <button type="button" onClick={() => ask(q)}
      className="text-left text-[11.5px] leading-snug px-2.5 py-1.5 rounded-lg bg-surface border border-ai/25 text-ink-2 hover:bg-ai-soft hover:border-ai/50 hover:text-ai-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring"><BrandCopy>{q}</BrandCopy></button>
  );
  const Ctas = () => (
    <div className="flex flex-wrap gap-2 mt-2.5">
      <button type="button" onClick={() => { requestDemo(); closeAssistant(); }} className="inline-flex items-center gap-1.5 rounded-full bg-[linear-gradient(110deg,#2457ea_0%,#4f46e5_52%,#7c3aed_100%)] text-white text-[11.5px] font-semibold px-3 py-1.5 hover:brightness-105 transition-[filter] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2">Book a demo <ArrowRight className="w-3 h-3" aria-hidden="true" /></button>
      <button type="button" onClick={openLeadForm} data-testid="aira-talk-sales" className="inline-flex items-center gap-1.5 rounded-full border border-ai/35 text-ai-ink text-[11.5px] font-semibold px-3 py-1.5 hover:bg-ai-soft transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring"><Phone className="w-3 h-3" aria-hidden="true" /> Talk to sales</button>
      <Link to="/help" onClick={() => setOpen(false)} className="inline-flex items-center gap-1.5 rounded-full border border-line text-ink-2 text-[11.5px] font-semibold px-3 py-1.5 hover:border-ai/35 hover:text-ai-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring"><BookOpen className="w-3 h-3" aria-hidden="true" /> Help Center</Link>
    </div>
  );

  // PERF-6: `m` + an explicit feature bundle instead of `motion` (which statically pulls
  // framer-motion's full drag + layout-projection set into whatever chunk imports it — and this
  // widget is a STATIC import of the eager landing entry chunk). The boundary lives here rather
  // than being inherited from MarketingMotion because App's MarketingAira gate also mounts this
  // widget standalone on /solutions, /help, /pricing … where no marketing provider is above it.
  // Nesting under MarketingMotion on the landing page is a no-op (same bundle, already loaded).
  return (
    <LazyMotion features={domAnimation} strict>
      <m.button ref={fabRef} type="button" onClick={toggleOpen} data-testid="aira-fab"
        aria-label={open ? "Close the Aira assistant" : "Open the Vidyapeeth360 Aira assistant"} aria-expanded={open}
        aria-hidden={fabHidden ? "true" : undefined}
        tabIndex={fabHidden ? -1 : 0}
        style={cookieClearance > 0 ? { bottom: `${cookieClearance}px` } : undefined}
        className={`fixed bottom-4 right-4 z-50 w-12 h-12 sm:bottom-6 sm:right-6 sm:w-14 sm:h-14 rounded-2xl grid place-items-center bg-white ring-1 ring-black/5 shadow-[0_12px_30px_-6px_rgba(255,107,26,0.45)] hover:shadow-[0_16px_38px_-8px_rgba(255,107,26,0.55)] transition-[box-shadow,transform,opacity] active:scale-[0.94] motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-orange ${fabHidden ? "pointer-events-none opacity-0" : "opacity-100"}`}>
        <AnimatePresence mode="wait" initial={false}>
          <m.span
            key={open ? "x" : "chat"}
            initial={motionOff ? false : { opacity: 0, transform: "rotate(-90deg)" }}
            animate={{ opacity: 1, transform: "rotate(0deg)" }}
            exit={motionOff ? { opacity: 0 } : { opacity: 0, transform: "rotate(90deg)" }}
            transition={{ duration: motionOff ? 0 : 0.18, ease: [0.23, 1, 0.32, 1] }}
          >
            {open ? <X className="w-6 h-6 text-orange-dark" aria-hidden="true" /> : <img src={LOGO_ICON} alt="" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />}
          </m.span>
        </AnimatePresence>
      </m.button>

      <AnimatePresence>
        {/* NON-modal dialog by design (no backdrop/scroll-lock; the page stays usable), so no
            aria-modal — that attribute would tell AT the rest of the page is inert when it
            isn't. Tab still cycles within the panel; Escape closes and restores focus. */}
        {open && (
          <m.div data-testid="aira-panel" data-motion-state={motionOff ? "off" : "running"} role="dialog" aria-label="Vidyapeeth360 Aira assistant"
            onKeyDown={(e) => {
              if (e.key === "Escape") { closeAssistant(); return; }
              // WP-17: trap Tab inside the dialog (role=dialog without a trap let focus walk
              // into the page behind the panel).
              if (e.key !== "Tab") return;
              const panel = e.currentTarget;
              const focusables = panel.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
              );
              if (!focusables.length) return;
              const first = focusables[0];
              const last = focusables[focusables.length - 1];
              if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
              else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
            }}
            initial={motionOff ? { opacity: 0 } : { opacity: 0, transform: "translate3d(0, 16px, 0) scale(0.96)" }} animate={{ opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" }} exit={motionOff ? { opacity: 0 } : { opacity: 0, transform: "translate3d(0, 12px, 0) scale(0.97)" }} transition={{ duration: motionOff ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
            style={{
              bottom: cookieClearance > 0 ? `${cookieClearance}px` : "6rem",
              // `dvh` (not `vh`) — a mobile browser's address bar dynamically shows/hides as the
              // page scrolls, and `100vh` is fixed to the LARGEST possible viewport (address bar
              // collapsed), so a `vh`-sized panel is taller than the space actually visible
              // whenever the bar is showing: the panel visibly jumps/resizes ("zooms") as the
              // bar toggles and can render clipped/overflowing — the exact failure class the rest
              // of this codebase already avoids with `dvh` (CookieConsent.js, the marketing nav
              // drawer's `max-h-[calc(100dvh-5.5rem)]`); this panel was the one place still on `vh`.
              maxHeight: cookieClearance > 0 ? `calc(100dvh - ${cookieClearance + 16}px)` : "calc(100dvh - 7.5rem)",
            }}
            className="fixed left-4 right-4 sm:left-auto sm:right-6 z-50 w-auto sm:w-[380px] max-w-[calc(100vw-2rem)] rounded-2xl overflow-hidden bg-surface border border-ai/25 shadow-[0_24px_70px_-12px_rgba(79,70,229,0.45)] origin-bottom-right flex flex-col">
            {/* Aira identity — cobalt-to-violet; the brand-logo FAB remains unchanged. */}
            <div className="px-4 py-3.5 flex items-center gap-3 bg-[linear-gradient(110deg,#2457ea_0%,#4f46e5_52%,#7c3aed_100%)] shrink-0">
              <div className="w-9 h-9 rounded-xl bg-white/20 ring-1 ring-inset ring-white/30 grid place-items-center shadow-[inset_0_1px_6px_rgba(255,255,255,0.35)]"><Sparkles className="w-5 h-5 text-white" aria-hidden="true" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm font-semibold leading-tight text-white">Aira AI Assistant</p>
                <p className="mt-0.5 text-[11.5px] leading-tight text-white/85">Public product guide · Help Center grounded</p>
              </div>
              <button type="button" onClick={closeAssistant} aria-label="Close the Aira assistant" data-testid="aira-close" className="w-7 h-7 rounded-lg grid place-items-center text-white/90 hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"><X className="w-4 h-4" aria-hidden="true" /></button>
            </div>

            {/* body — live LLM answers + Help Center article links */}
            <div ref={scrollRef} role="log" aria-live="polite" aria-relevant="additions text" aria-busy={busy} className="px-3.5 py-3.5 space-y-3 flex-1 min-h-0 max-h-[min(58dvh,420px)] overflow-y-auto bg-ai-soft/40">
              {messages.map((m) => (
                m.role === "user" ? (
                  <div key={m.id} className="flex justify-end">
                    <div className="text-sm rounded-2xl rounded-br-md px-3.5 py-2 max-w-[85%] bg-[linear-gradient(110deg,#2457ea_0%,#4f46e5_52%,#7c3aed_100%)] text-white shadow-sm whitespace-pre-wrap">{m.text}</div>
                  </div>
                ) : (
                  <div key={m.id} className="flex flex-col items-start gap-2">
                    <div className="text-sm rounded-2xl rounded-tl-md px-3.5 py-2.5 max-w-[90%] bg-surface border border-ai/20 text-ink-2 shadow-[0_2px_12px_rgba(79,70,229,0.08)]">
                      {m.welcome ? (
                        <>
                          <p className="leading-relaxed"><span className="font-semibold text-ink">Hi! I'm Aira — the <BrandWordmarkText /> AI assistant.</span><br />Ask about features, pricing, modules, setup, migration or support and I'll answer from our public guides. For anything about your own school's data, sign in — I can't see it from here.</p>
                          <Ctas />
                        </>
                      ) : (
                        <>
                          <Markdown content={m.text} className="leading-relaxed [&>*+*]:mt-1.5" />
                          {m.articles?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-ai/15" data-testid="aira-articles">
                              <p className="text-[11px] font-semibold text-ai-ink mb-1 flex items-center gap-1.5"><BookOpen className="w-3 h-3" aria-hidden="true" /> From the Help Center</p>
                              <ul className="space-y-1">
                                {m.articles.map((a) => (
                                  <li key={a.slug}>
                                    <Link to={`/help?article=${encodeURIComponent(a.slug)}`} onClick={() => setOpen(false)}
                                      className="inline-flex items-start gap-1 text-[12px] text-ai-ink font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring rounded">
                                      <ArrowUpRight className="w-3 h-3 mt-0.5 shrink-0" aria-hidden="true" />{a.title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {m.error && <Ctas />}
                        </>
                      )}
                    </div>
                    {m.welcome && <div className="flex flex-col items-start gap-1.5 pl-1">{AIRA_STARTERS.map((q) => <Chip key={q} q={q} />)}</div>}
                    {m.suggestions?.length > 0 && m.id === lastAssistantId && !quotaGate && (
                      <div className="flex flex-col items-start gap-1.5 pl-1" data-testid="aira-suggestions">{m.suggestions.map((q) => <Chip key={q} q={q} />)}</div>
                    )}
                  </div>
                )
              ))}
              {busy && (
                <div className="flex items-center gap-2" aria-hidden="true">
                  <div className="rounded-2xl rounded-tl-md px-3.5 py-2.5 bg-surface border border-ai/20 flex items-center gap-2">
                    <span className="flex gap-1" aria-hidden="true">{[0, 1, 2].map((i) => <span key={i} className={`w-1.5 h-1.5 rounded-full bg-ai/70 ${motionOff ? "" : "animate-bounce"}`} style={{ animationDelay: `${i * 0.15}s` }} />)}</span>
                    <span className="text-[12px] text-ink-subtle">Aira is thinking…</span>
                  </div>
                </div>
              )}
            </div>
            <span className="sr-only" role="status" aria-live="polite" data-testid="aira-busy-status">
              {busy ? "Aira is thinking." : ""}
            </span>

            {/* lead capture — required once the 50 free questions are used; always available via "Talk to sales" */}
            {leadOpen && !leadDone && (
              <form onSubmit={(event) => { event.preventDefault(); submitLead(); }} className="border-t border-line p-3 bg-surface space-y-2 shrink-0" data-testid="aira-lead-form">
                <p className="text-xs font-semibold text-ink">{quotaGate && !hardCap ? "You've used your 50 free questions. Send your school and contact details and our team will follow up — you can then keep chatting up to 150 questions in total." : "Leave your details to send an enquiry to our sales and support team."}</p>
                {/* FORM-007: VISIBLE labels, not placeholder-as-label — a placeholder disappears the
                    moment the visitor types (taking the required marker with it) and gives a
                    voice-control user a name that no longer matches what they can see. */}
                <div>
                  <label htmlFor="aira-lead-school" className="block text-[11px] font-semibold text-ink-2 mb-1">School name <span className="text-danger" aria-hidden="true">*</span><span className="sr-only"> (required)</span></label>
                  <input id="aira-lead-school" ref={leadSchoolRef} value={lead.school} onChange={(e) => setLead((l) => ({ ...l, school: e.target.value }))} placeholder="For example, Sunrise Public School" className="input-base w-full py-2 text-sm" data-testid="aira-lead-school" />
                </div>
                <div>
                  <label htmlFor="aira-lead-name" className="block text-[11px] font-semibold text-ink-2 mb-1">Your name</label>
                  <input id="aira-lead-name" value={lead.name} onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))} placeholder="Full name" className="input-base w-full py-2 text-sm" />
                </div>
                <div>
                  <label htmlFor="aira-lead-phone" className="block text-[11px] font-semibold text-ink-2 mb-1">Phone</label>
                  <input id="aira-lead-phone" value={lead.phone} onChange={(e) => setLead((l) => ({ ...l, phone: e.target.value }))} placeholder="+91 98765 43210" inputMode="tel" className="input-base w-full py-2 text-sm" data-testid="aira-lead-phone" />
                </div>
                <div>
                  <label htmlFor="aira-lead-email" className="block text-[11px] font-semibold text-ink-2 mb-1">Email</label>
                  <input id="aira-lead-email" value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))} placeholder="principal@yourschool.in" inputMode="email" className="input-base w-full py-2 text-sm" />
                </div>
                {/* FORM-001: notice AT collection. This form files a sales lead AND a support ticket
                    carrying an excerpt of the chat, so it must say so and link the Privacy Policy. */}
                <p className="text-[11px] leading-relaxed text-ink-subtle" data-testid="aira-lead-privacy-note">
                  School name plus a phone or email is enough. We&rsquo;ll use these details, and a short extract of this chat, only to answer your enquiry — see our{" "}
                  <Link to="/privacy-policy" onClick={() => setOpen(false)} className="font-semibold text-ai-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring rounded">Privacy Policy</Link>.
                  {" "}You can ask us to delete them at any time.
                </p>
                {leadError && <p role="alert" className="text-xs text-danger">{leadError}</p>}
                <Captcha onToken={setCaptchaToken} action="aira_lead" className="flex justify-center" />
                <div className="flex gap-2">
                  <button type="submit" disabled={leadBusy || !leadValid}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[linear-gradient(110deg,#2457ea_0%,#4f46e5_52%,#7c3aed_100%)] text-white px-3 py-2 text-sm font-semibold disabled:opacity-50 hover:brightness-105 transition-[filter] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2" data-testid="aira-lead-submit">
                    {leadBusy ? "Sending…" : "Send enquiry"}
                  </button>
                  {!quotaGate && (
                    <button type="button" onClick={closeLeadForm} className="rounded-xl border border-line text-ink-2 px-3 py-2 text-sm hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring" aria-label="Cancel and close the contact form">Cancel</button>
                  )}
                </div>
              </form>
            )}

            {/* input — Aira gradient send; gated (not hidden) while the lead form is required */}
            <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-3 border-t border-line flex items-end gap-2 bg-surface shrink-0">
              {!leadOpen && !quotaGate && (
                <button type="button" onClick={openLeadForm} title="Talk to sales" aria-label="Talk to sales — leave your contact details" data-testid="aira-lead-open"
                  className="p-2 rounded-lg text-ink-subtle hover:bg-ai-soft hover:text-ai-ink shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring"><UserPlus className="w-5 h-5" aria-hidden="true" /></button>
              )}
              <label htmlFor="aira-input" className="sr-only">Message Aira</label>
              {/* WP-17: auto-grow up to the max-h cap (rows=1 alone left long input scrolling
                  inside one line and the placeholder wrapping outside the border). The height
                  resets when the value empties (send/clear), not only on typing. */}
              <textarea id="aira-input" ref={inputRef} rows={1} value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  const el = e.target;
                  el.style.height = "auto";
                  el.style.height = e.target.value ? `${Math.min(el.scrollHeight, 96)}px` : "";
                }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                placeholder={hardCap ? "Chat limit reached — your enquiry is with sales and support" : quotaGate ? "Share your details above to keep chatting" : "Ask about pricing, modules, setup…"} data-testid="aira-input" disabled={quotaGate}
                className="flex-1 resize-none max-h-24 overflow-y-auto text-ellipsis rounded-xl border border-line bg-surface px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-subtle placeholder:truncate focus:outline-none focus:border-ai focus:ring-2 focus:ring-ai/20 transition-[border-color,box-shadow] disabled:opacity-60" />
              <button type="submit" disabled={!input.trim() || busy || quotaGate} aria-label="Send message" data-testid="aira-send" className="w-10 h-10 shrink-0 rounded-full bg-[linear-gradient(110deg,#2457ea_0%,#4f46e5_52%,#7c3aed_100%)] text-white grid place-items-center shadow-[0_6px_16px_-4px_rgba(79,70,229,0.55)] hover:brightness-105 active:scale-95 motion-reduce:active:scale-100 disabled:opacity-40 disabled:pointer-events-none transition-[filter,transform]"><Send className="w-4 h-4" aria-hidden="true" /></button>
            </form>
            <div className="px-3.5 pb-3 pt-0.5 bg-surface shrink-0 flex items-center justify-between gap-2">
              <Link to="/help" onClick={() => setOpen(false)} className="inline-flex items-center gap-1 text-[12px] text-ai-ink font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring rounded" data-testid="aira-browse-help">Browse the full Help Center <ArrowRight className="w-3 h-3" aria-hidden="true" /></Link>
              {remaining != null && remaining <= 10 && !leadDone && (
                <span className="text-[11px] text-ink-subtle tabular-nums shrink-0" data-testid="aira-remaining">{remaining} free question{remaining === 1 ? "" : "s"} left</span>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
}
